'use client';

import { useState } from 'react';
import Image from 'next/image';
import { formatDistanceToNow } from 'date-fns';
import PagesHeader from '@/components/PagesHeader/PagesHeader';
import { PublicSignUpDialog } from '@/components/public/PublicSignupDialog';
import { Button } from '@/components/ui/button';
import { ForumResponse, IGroup } from '@/modules/groups/types';
import { usePublicGroupsList } from '@/api/usePublicGroupsList';

type SampleGroupPost = {
  keywords: string[];
  title: string;
  fullName: string;
  neighborhood: string | null;
  timeLabel: string;
};

const sampleGroupPosts: SampleGroupPost[] = [
  {
    keywords: ['sleep', 'nap', 'bedtime'],
    title: 'How do I get my baby on a sleep schedule?',
    fullName: 'Anna',
    neighborhood: 'Rice Village',
    timeLabel: '2 hrs ago',
  },
  {
    keywords: ['twin', 'multiples'],
    title: 'I’m expecting twins in a month, any advice?',
    fullName: 'Destiny',
    neighborhood: 'Afton Oaks',
    timeLabel: '3 hrs ago',
  },
  {
    keywords: ['dad', 'father', 'single parent'],
    title: 'Looking for fellow dads',
    fullName: 'Michael',
    neighborhood: 'Memorial Forest',
    timeLabel: '6 hrs ago',
  },
  {
    keywords: ['school', 'kindergarten', 'grade', 'homeschool'],
    title: 'Is he ready for 1st grade?',
    fullName: 'Amy',
    neighborhood: 'Aldine',
    timeLabel: '6 hrs ago',
  },
  {
    keywords: ['pet', 'animal', 'dog', 'cat'],
    title: 'Are animals good for kids?',
    fullName: 'Brandy',
    neighborhood: 'Rice Village',
    timeLabel: '13 hrs ago',
  },
  {
    keywords: ['milestone', 'walking', 'crawl', 'talk'],
    title: 'Shouldn’t she be walking?',
    fullName: 'Joseph',
    neighborhood: 'Bay Forest',
    timeLabel: '15 hrs ago',
  },
  {
    keywords: ['feed', 'food', 'eat', 'nutrition', 'meal'],
    title: 'Has anyone tried feeding sushi to a toddler?',
    fullName: 'Sara',
    neighborhood: 'Highland Heights',
    timeLabel: '18 hrs ago',
  },
  {
    keywords: ['behav', 'bite', 'discipline', 'tantrum'],
    title: 'Biting?!! Help!',
    fullName: 'Beau',
    neighborhood: 'Northline',
    timeLabel: '20 hrs ago',
  },
  {
    keywords: ['ivf', 'fertility', 'ttc', 'pregnan'],
    title: 'IVF journeys',
    fullName: 'Tina',
    neighborhood: 'Museum District',
    timeLabel: '1 day ago',
  },
  {
    keywords: ['daycare', 'preschool', 'nursery', 'nanny'],
    title: 'Best daycares in the area?',
    fullName: 'Mia',
    neighborhood: 'Almeda',
    timeLabel: '5 hrs ago',
  },
  {
    keywords: ['potty', 'toilet', 'diaper'],
    title: 'Potty training tips?',
    fullName: 'Jen',
    neighborhood: 'Forest Hills',
    timeLabel: '8 hrs ago',
  },
  {
    keywords: ['playdate', 'play', 'meet'],
    title: 'Anyone up for a weekend playdate?',
    fullName: 'Chris',
    neighborhood: 'Prospect Heights',
    timeLabel: '4 hrs ago',
  },
];

function pickSamplePost(groupName: string, index: number): SampleGroupPost {
  const name = groupName.toLowerCase();
  const match = sampleGroupPosts.find((s) =>
    s.keywords.some((k) => name.includes(k))
  );
  return match ?? sampleGroupPosts[index % sampleGroupPosts.length];
}

export default function PublicGroupsClient({
  initialGroups,
}: {
  initialGroups: ForumResponse<IGroup> | null;
}) {
  const [searchText, setSearchText] = useState('');
  const [showPublicSignupDialog, setShowPublicSignupDialog] = useState(false);

  const { data, isLoading, isError } = usePublicGroupsList(initialGroups);

  const groupItems: IGroup[] =
    data?.pages.flatMap((page) => page.responseObject?.rows || []) || [];
  const displayItems = groupItems.slice(0, 8);

  const handleGroupClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPublicSignupDialog(true);
  };

  return (
    <div className="min-h-screen">
      <PagesHeader
        searchText={searchText}
        setSearchText={setSearchText}
        searchPlaceholder="Search all groups"
        tabs={[
          { label: 'All Groups', href: '/public/groups' },
          { label: 'Your Groups', href: '#' },
          { label: 'Saved Groups', href: '#' },
        ]}
        activeTab="/public/groups"
      />

      <div className="grid sm:grid-cols-[repeat(auto-fit,minmax(200px,1fr))] md:grid-cols-[repeat(auto-fit,minmax(200px,.5fr))] gap-5 xl:w-[90%] mt-6">
        {isLoading ? (
          Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="animate-pulse">
              <div className="bg-gray-200 aspect-[5/4] rounded-lg mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-1"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))
        ) : isError ? (
          <div className="col-span-full text-center py-8">
            Error loading groups.
          </div>
        ) : displayItems.length === 0 ? (
          <div className="col-span-full text-center py-8">
            No groups found.
          </div>
        ) : (
          displayItems.map((group, index) => {
            const src =
              group.thumbnailImage ??
              group.coverImage ??
              'https://st.depositphotos.com/3647117/5113/v/450/depositphotos_51136523-stock-illustration-big-family-silhouettes.jpg';

            const sample = pickSamplePost(group.name, index);
            const displayTitle = group.latestPost?.title ?? sample.title;
            const displayFullName =
              group.latestPost?.fullName ?? sample.fullName;
            const displayNeighborhood =
              group.latestPost?.neighborhood ?? sample.neighborhood;
            const displayTimeLabel = group.latestPost
              ? formatDistanceToNow(new Date(group.latestPost.createdAt), {
                  addSuffix: true,
                })
              : sample.timeLabel;

            return (
              // A div with onClick could not be reached or activated by
              // keyboard. This one opens the signup dialog rather than
              // navigating, so it takes button semantics; a real <button>
              // cannot legally wrap the <h3> below, hence role + tabIndex.
              <div
                key={`${group.id}-${index}`}
                role="button"
                tabIndex={0}
                aria-label={`Sign up to view ${group.name}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleGroupClick(e as unknown as React.MouseEvent);
                  }
                }}
                className="flex flex-col min-w-[200px] cursor-pointer animate-in fade-in-0 slide-in-from-bottom-4 duration-500 rounded-lg"
                style={{
                  animationDelay: `${Math.min(index * 100, 1000)}ms`,
                  animationFillMode: 'both',
                }}
                onClick={handleGroupClick}
              >
                <div className="relative w-full aspect-[5/4] rounded-lg overflow-hidden bg-gray-100">
                  <Image
                    src={src}
                    alt=""
                    fill
                    className="object-cover"
                    unoptimized
                    loading="lazy"
                  />
                </div>

                <h3 className="text-[16.5px] font-medium truncate w-full capitalize mt-2 font-[Merriweather]">
                  {group.name}
                </h3>

                <p className="text-base font-semibold truncate w-full capitalize">
                  {displayTitle}
                </p>

                <p className="text-sm font-normal truncate w-full capitalize">
                  {displayTimeLabel} -{displayFullName?.split(' ')[0]}
                  {displayNeighborhood ? ` - ${displayNeighborhood}` : ''}
                </p>
              </div>
            );
          })
        )}
      </div>

      {groupItems.length > 8 && (
        <div className="text-center my-8 py-8 px-4">
          <div className="max-w-md mx-auto">
            <h3 className="text-2xl font-bold mb-2">See More Groups!</h3>
            <p className="text-gray-600 mb-6">
              You have viewed 8 groups. Join our community to explore all groups
              and connect with neighbors!
            </p>
            <Button
              onClick={() => setShowPublicSignupDialog(true)}
              className="bg-[#948780] hover:bg-[#7a6b64] text-black px-8 py-4 rounded-full font-bold"
            >
              Sign Up
            </Button>
          </div>
        </div>
      )}

      <PublicSignUpDialog
        open={showPublicSignupDialog}
        onOpenChange={setShowPublicSignupDialog}
        message="Sign up to join groups, participate in discussions, and connect with your community!"
      />
    </div>
  );
}
